import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { app } from "base.js";
import { UserContext } from "context/User";
import { Button, TextField, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import ReactGA from "react-ga4";
import { CartContext } from "context/Cart";
import axios from "axios";
import { API_URL } from "config";

interface LoginProps {
    setActiveAuth: (auth: string) => void,
}

const Login = ({ setActiveAuth }: LoginProps) => {
    const { currentUser } = useContext(UserContext);
    const { clearCart } = useContext(CartContext);

    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [validation, setValidation] = useState({ email: false, password: false });
    const [dialogOpen, setDialogOpen] = useState(false);
    const [apiDown, setApiDown] = useState(false);
    const [retryingApi, setRetryingApi] = useState(false);

    // Final catch for a fully-down API (bad deploy, dead server). Uses raw
    // axios on purpose: makeAxiosCall rejects with error.response, which
    // erases the network-vs-HTTP distinction this check depends on. Guarded
    // against false positives: timeouts do not count (a slow connection must
    // never lock users out), HTTP errors do not count (the API is up), and it
    // takes TWO consecutive network-level failures before the page flips.
    const failStreak = useRef(0);
    const checkApiHealth = useCallback(() => {
        setRetryingApi(true);
        axios.get(API_URL + "health", { timeout: 7000 }).then(() => {
            failStreak.current = 0;
            setApiDown(false);
        }).catch((e) => {
            const isTimeout = e?.code === "ECONNABORTED" || e?.code === "ETIMEDOUT";
            const isNetworkFailure = !e?.response && !isTimeout;
            if (isNetworkFailure) {
                failStreak.current++;
                if (failStreak.current >= 2) {
                    setApiDown(true);
                } else {
                    setTimeout(checkApiHealth, 2500);
                }
            } else {
                failStreak.current = 0;
                setApiDown(false);
            }
        }).finally(() => setRetryingApi(false));
    }, []);

    useEffect(() => {
        checkApiHealth();
    }, []);

    useEffect(() => {
        if (!apiDown) return;
        const interval = setInterval(checkApiHealth, 10000);
        return () => clearInterval(interval);
    }, [apiDown]);

    const urlParams = new URLSearchParams(window.location.search);
    const newReseller = urlParams.get("newReseller");

    const handleLogin = useCallback(async (e) => {
        e.preventDefault();
        clearCart();

        localStorage.removeItem("logout-event");

        const { email, password } = e.target.elements;

        let emailPresent = false;
        let passwordPresent = false;

        if (email.value && email.value.length > 0) {
            emailPresent = true;
        }

        if (password.value && password.value.length > 0) {
            passwordPresent = true;
        }

        if (emailPresent && passwordPresent) {
            try {
                setLoading(true);
                // ReactGA.initialize("G-TLB5SZ3EZD");
                ReactGA.send({ hitType: "event", eventCategory: "auth", eventAction: "login", eventLabel: email.value });
                // makeAxiosCall(
                //     "post",
                //     "email/login",
                //     { email: email.value }
                // );

                await app.auth().signInWithEmailAndPassword(email.value, password.value);
            } catch (error) {
                console.error(error);
                const errorCode = error.code.substring(5);

                let errorMessage = "Something went wrong. Please try again.";
                if (errorCode == "invalid-email" || errorCode == "wrong-password") {
                    errorMessage = "Incorrect email or password";
                }

                setLoading(false);
                setError(errorMessage);
            }
        } else {
            setValidation({ email: !emailPresent, password: !passwordPresent });
        }
    }, []);

    const openDialog = () => {
        setDialogOpen(true);
    };

    const closeDialog = () => {
        setDialogOpen(false);
    };

    const handleResellerApplication = () => {
        const search = new URLSearchParams();
        search.set("type", "signup");
        search.set("newReseller", "true");
        window.location.search = search.toString();
    };

    if (currentUser) {
        clearCart();
        return <Navigate to="/" />;
    }

    if (apiDown) {
        return (
            <div style={{ textAlign: "center", padding: "60px 24px" }}>
                <h1>Be right back!</h1>
                <p style={{ fontSize: "1.1em", maxWidth: 420, margin: "16px auto" }}>
                    The portal is down for a second. Sorry for the interruption, it will be back up shortly.
                </p>
                <p style={{ color: "gray" }}>
                    This page checks again automatically every few seconds.
                </p>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={retryingApi}
                    onClick={checkApiHealth}
                >
                    {retryingApi ? <CircularProgress color="inherit" size={22} /> : "Try Now"}
                </Button>
            </div>
        );
    }

    return (
        <div>
            <h1>Log in</h1>
            <p className="underline" onClick={() => setActiveAuth("signup")}>
                Create an account
            </p>
            <form onSubmit={handleLogin}>
                <TextField
                    className="input-full"
                    label="Email"
                    margin="normal"
                    variant="outlined"
                    inputProps={{
                        name: "email",
                    }}
                    error={validation.email}
                    helperText={validation.email ? "Required" : ""}
                />
                <TextField
                    className="input-full"
                    label="Password"
                    margin="normal"
                    variant="outlined"
                    type="password"
                    autoComplete="current-password"
                    inputProps={{
                        name: "password",
                    }}
                    error={validation.password}
                    helperText={validation.password ? "Required" : ""}
                />
                <Button
                    style={{ width: "100%" }}
                    type="submit"
                    variant="contained"
                    color="primary"
                >
                    {loading ?
                        <CircularProgress
                            color="inherit"
                            size={25}
                        /> :
                        "Log In"
                    }
                </Button>

                <p
                    className="underline fine"
                    onClick={() => setActiveAuth("forgot")}
                >
                    Forgot your password?
                </p>
                {error && <p className="error">{error}</p>}
            </form>

            <Button
                style={{ width: "100%", marginTop: 50 }}
                variant="contained"
                color="secondary"
                onClick={openDialog}
            >
                Apply to Become a Reseller
            </Button>

            <Dialog
                open={dialogOpen}
                onClose={closeDialog}
            >
                <DialogTitle>
                    Interested in becoming a reseller?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        First, sign up for an account. From there, you can fill out our New
                        Reseller application. Someone from our team will contact you with
                        follow up steps afterwards.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleResellerApplication} autoFocus>
                        Take me there!
                    </Button>
                    <Button onClick={closeDialog}>
                        No Thanks
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default Login;
